import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Plus } from 'lucide-react';
import type { AdminAuthorInput } from '@repo/validation';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import { cn } from '@repo/ui/src/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/src/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@repo/ui/src/components/ui/sheet';
import { useAdminBlogAuthors, useCreateBlogAuthor, useUpdateBlogAuthor, type AdminBlogAuthor } from '../hooks/useBlog';

// Mirrors adminAuthorSchema.persona in @repo/validation. Kept as a literal
// because the schema exports no max; if that cap moves, move this with it.
const PERSONA_MAX = 8000;

export function BlogAuthorsPage() {
  // `null` = closed, `'new'` = create sheet, an author = edit sheet for it.
  const [sheetTarget, setSheetTarget] = useState<AdminBlogAuthor | 'new' | null>(null);
  const { data: authors, isLoading, error } = useAdminBlogAuthors();

  return (
    <div className="flex flex-col gap-6">
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-surface-600 hover:underline w-fit">
        <ArrowLeft className="h-4 w-4" /> Quay lại danh sách bài viết
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-surface-900">Tác giả</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tác giả có thể được gán cho bài viết blog. Cần ít nhất một tác giả để lưu bài viết.
          </p>
        </div>
        <Button type="button" onClick={() => setSheetTarget('new')}>
          <Plus className="mr-1.5 h-4 w-4" /> Tác giả mới
        </Button>
      </div>

      <div className="rounded-md border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tên</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Vai trò</TableHead>
              <TableHead className="text-right">Hành động</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                  Đang tải...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-destructive">
                  Không thể tải danh sách tác giả. Vui lòng thử lại sau.
                </TableCell>
              </TableRow>
            ) : !authors || authors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                  Chưa có tác giả nào. Tạo một tác giả để bắt đầu viết bài.
                </TableCell>
              </TableRow>
            ) : (
              authors.map((author) => (
                <TableRow key={author.id}>
                  <TableCell className="py-2 font-medium text-surface-900">{author.name}</TableCell>
                  <TableCell className="py-2 text-surface-600">{author.slug}</TableCell>
                  <TableCell className="py-2 text-surface-600">{author.role ?? '—'}</TableCell>
                  <TableCell className="py-2 text-right">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setSheetTarget(author)}>
                      Sửa
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AuthorSheet
        target={sheetTarget}
        onOpenChange={(open) => {
          if (!open) setSheetTarget(null);
        }}
      />
    </div>
  );
}

function AuthorSheet({
  target,
  onOpenChange,
}: {
  target: AdminBlogAuthor | 'new' | null;
  onOpenChange: (open: boolean) => void;
}) {
  const author = target === 'new' ? null : target;
  const isNew = target === 'new';

  return (
    <Sheet open={target !== null} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isNew ? 'Tác giả mới' : 'Sửa tác giả'}</SheetTitle>
          <SheetDescription>
            Slug và tên là bắt buộc. Các trường còn lại có thể để trống và bổ sung sau.
          </SheetDescription>
        </SheetHeader>
        {/* Keyed by which author is open so state resets between "new" and
         *  editing a different author, without needing to reset every field
         *  by hand — same idiom as AssignPlanSheet. */}
        <AuthorForm key={target === 'new' ? 'new' : author?.id ?? 'closed'} author={author} onOpenChange={onOpenChange} />
      </SheetContent>
    </Sheet>
  );
}

function AuthorForm({
  author,
  onOpenChange,
}: {
  author: AdminBlogAuthor | null;
  onOpenChange: (open: boolean) => void;
}) {
  const isNew = !author;
  const createAuthor = useCreateBlogAuthor();
  const updateAuthor = useUpdateBlogAuthor(author?.id ?? '');

  const [slug, setSlug] = useState(author?.slug ?? '');
  const [name, setName] = useState(author?.name ?? '');
  const [role, setRole] = useState(author?.role ?? '');
  const [bio, setBio] = useState(author?.bio ?? '');
  const [avatarUrl, setAvatarUrl] = useState(author?.avatarUrl ?? '');
  const [persona, setPersona] = useState(author?.persona ?? '');

  const isPending = createAuthor.isPending || updateAuthor.isPending;
  const canSubmit = !isPending && !!slug.trim() && !!name.trim() && persona.length <= PERSONA_MAX;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    const payload: AdminAuthorInput = {
      slug: slug.trim(),
      name: name.trim(),
      role: role.trim() || null,
      bio: bio.trim() || null,
      avatarUrl: avatarUrl.trim() || null,
      persona: persona.trim() || null,
    };

    if (isNew) {
      createAuthor.mutate(payload, { onSuccess: () => onOpenChange(false) });
    } else {
      updateAuthor.mutate(payload, { onSuccess: () => onOpenChange(false) });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 py-4">
      <div className="space-y-2">
        <Label htmlFor="author-name">Tên</Label>
        <Input id="author-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>

      <div className="space-y-2">
        <Label htmlFor="author-slug">Slug</Label>
        <Input
          id="author-slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="ví dụ: nguyen-van-a"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="author-role">Vai trò (tuỳ chọn)</Label>
        <Input
          id="author-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="ví dụ: Đồng sáng lập LopHoc.app"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="author-bio">Tiểu sử (tuỳ chọn)</Label>
        <Textarea id="author-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={3} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="author-avatar">Ảnh đại diện (URL, tuỳ chọn)</Label>
        <Input
          id="author-avatar"
          value={avatarUrl}
          onChange={(e) => setAvatarUrl(e.target.value)}
          placeholder="https://..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="author-persona">Persona (tuỳ chọn)</Label>
        <Textarea
          id="author-persona"
          value={persona}
          onChange={(e) => setPersona(e.target.value)}
          rows={10}
          placeholder="Ví dụ: giọng thân thiện, hay dùng ví dụ thực tế từ lớp học nhỏ..."
        />
        <div className="flex items-start justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            Ghi chú riêng để mô tả giọng viết của tác giả này, dùng để mồi cho AI khi soạn bài (nút "Sao chép prompt
            AI" trong khung soạn). Không hiển thị công khai.
          </p>
          {/* The limit was invisible until the server rejected the save, which
              is the worst moment to learn about it — a long brief is exactly
              the kind of text nobody wants to retype. */}
          <span
            className={cn(
              'shrink-0 text-xs tabular-nums',
              persona.length > PERSONA_MAX ? 'font-medium text-destructive' : 'text-muted-foreground',
            )}
          >
            {persona.length.toLocaleString('vi-VN')} / {PERSONA_MAX.toLocaleString('vi-VN')}
          </span>
        </div>
      </div>

      <SheetFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
          Huỷ
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {isPending ? 'Đang lưu...' : isNew ? 'Tạo tác giả' : 'Lưu'}
        </Button>
      </SheetFooter>
    </form>
  );
}
